import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { adminApi } from '../services/ApiService'
import SubscriptionService from '../services/SubscriptionService'
import InventoryService from '../services/InventoryService'
import ClientService from '../services/ClientService'
import ticketService from '../services/TicketService'
import { exportToCSV } from '../utils/exportCSV'
import Pagination from '../components/Pagination'
import ClientDetail from '../components/ClientDetail'
import {
  Users, Search, Mail, Calendar, Package, CreditCard,
  Gift, CheckCircle, XCircle, AlertTriangle, ChevronDown, Shield, Clock,
  DollarSign, Download, MessageSquare, Plus, Trash2, Edit3, Power, Wallet
} from 'lucide-react'
import './ClientsHub.css'

const ITEMS_PER_PAGE = 15

const PLANS = {
  free: { name: 'Sin plan', color: '#6b7280', icon: '🆓', devices: 0, rooms: 0 },
  basico: { name: 'Básico', color: '#3b82f6', icon: '⚡', devices: 3, rooms: 2 },
  pro: { name: 'Pro', color: '#8b5cf6', icon: '🚀', devices: 10, rooms: 5 },
  comercial: { name: 'Comercial', color: '#f59e0b', icon: '🏢', devices: '∞', rooms: '∞' },
}

// Fallback values — overridden at runtime by products from Inventario
const DEFAULT_PLAN_PRICES = {
  basico: 9990,
  pro: 14990,
  comercial: 44990,
}
const ANNUAL_PLAN_PRICES = {
  basico: 99990,
  pro: 149990,
  comercial: 449990,
}
const DEFAULT_DEVICE_ECONOMICS = {
  'GP-BOX': { cost: 80000, price: 389900 },
  'GP-MINI': { cost: 18000, price: 49990 },
  'GP-CAM': { cost: 35000, price: 129900 },
  'GP-HIDRO': { cost: 95000, price: 459900 },
  'GP-RELAY': { cost: 110000, price: 499900 },
  'GP-DIM': { cost: 0, price: 0 },
}

export default function ClientsHub() {
  const [tab, setTab] = useState('overview') // 'overview' | 'subscriptions' | 'garantias' | 'crm'
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [confirmModal, setConfirmModal] = useState({ show: false, client: null, action: null, newPlan: null, whitelistPlan: null })
  const [processing, setProcessing] = useState(false)
  const [planDropdown, setPlanDropdown] = useState(null)
  const [manualPayModal, setManualPayModal] = useState({ show: false, client: null, plan: 'pro', billingCycle: 'monthly' })
  const isInteractingRef = useRef(false)
  const clientsRef = useRef([])
  const [products, setProducts] = useState([])

  const [bomSummary, setBomSummary] = useState({})

  // Warranties & CRM state
  const [warranties, setWarranties] = useState([])
  const [clientNotes, setClientNotes] = useState([])
  const [warrantyForm, setWarrantyForm] = useState(null)
  const [noteForm, setNoteForm] = useState(null)
  const [selectedClient, setSelectedClient] = useState(null)
  const [supportTickets, setSupportTickets] = useState([])
  const [detailClient, setDetailClient] = useState(null)
  // Build dynamic lookups from BOM costs + products
  const { planPrices, deviceEconomics } = useMemo(() => {
    const pp = { ...DEFAULT_PLAN_PRICES }
    const de = { ...DEFAULT_DEVICE_ECONOMICS }
    // BOM costs override device cost
    for (const [model, parts] of Object.entries(bomSummary)) {
      const bomCost = (parts || []).reduce((s, p) => s + (p.lineCost || 0), 0)
      if (bomCost > 0) {
        if (!de[model]) de[model] = { cost: 0, price: 0 }
        de[model] = { ...de[model], cost: bomCost }
      }
    }
    // Products can override sale prices and subscription prices
    for (const p of products) {
      if (p.category === 'suscripcion' && p.sku) {
        const key = p.sku.toLowerCase()
        if (pp[key] !== undefined) pp[key] = p.salePrice
      } else if (p.category === 'dispositivo' && p.sku) {
        const model = p.sku.toUpperCase()
        if (!de[model]) de[model] = { cost: 0, price: 0 }
        if (p.salePrice) de[model] = { ...de[model], price: p.salePrice }
        if (p.costPrice && !bomSummary[model]) de[model] = { ...de[model], cost: p.costPrice }
      }
    }
    return { planPrices: pp, deviceEconomics: de }
  }, [products, bomSummary])

  useEffect(() => {
    fetchAllClients(true)
    InventoryService.listProducts().then(res => setProducts(res.products || [])).catch(() => { })
    InventoryService.getDashboard().then(res => {
      if (res.success && res.bomSummary) setBomSummary(res.bomSummary)
    }).catch(() => { })
    ClientService.listWarranties().then(res => setWarranties(res.warranties || [])).catch(() => { })
    ClientService.listClientNotes().then(res => setClientNotes(res.notes || [])).catch(() => { })
    ticketService.listTickets().then(res => setSupportTickets(res.tickets || [])).catch(() => { })
    const interval = setInterval(() => {
      if (!isInteractingRef.current) fetchAllClients(false)
    }, 60000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    isInteractingRef.current = confirmModal.show || planDropdown !== null || manualPayModal.show
  }, [confirmModal.show, planDropdown, manualPayModal.show])

  // Close plan dropdown on click outside (replaces backdrop div to avoid mobile z-index issues)
  useEffect(() => {
    if (!planDropdown) return
    const handler = (e) => {
      if (!e.target.closest('.ch-plan-dropdown') && !e.target.closest('.ch-plan-btn')) {
        setPlanDropdown(null)
      }
    }
    const timer = setTimeout(() => document.addEventListener('click', handler), 10)
    return () => { clearTimeout(timer); document.removeEventListener('click', handler) }
  }, [planDropdown])

  useEffect(() => { setCurrentPage(1) }, [filter, searchTerm, tab])

  const fetchAllClients = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true)
      const data = await adminApi.get('/admin/clients')
      const enriched = (data.clients || []).map(client => {
        const isTrial = client.isTrial === true
        let trialExpired = false
        let trialDaysRemaining = null
        if (isTrial && client.subscriptionExpiresAt) {
          const diffMs = new Date(client.subscriptionExpiresAt) - new Date()
          trialDaysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
          trialExpired = diffMs <= 0
        }
        // Detectar si la suscripción venció (tiene fecha de expiración pasada y no está activa)
        const isActive = client.subscriptionActive !== false
        const hasExpiration = !!client.subscriptionExpiresAt
        const expirationPassed = hasExpiration && new Date(client.subscriptionExpiresAt) < new Date()
        // Vencida = expiración pasada realmente. Si no tiene expiración o no pasó, es Bloqueada (bloqueo manual)
        const isExpired = !isActive && expirationPassed
        return {
          ...client,
          subscriptionActive: isActive,
          plan: client.plan || 'free',
          isTrial, trialExpired, trialDaysRemaining,
          isWhitelisted: client.isWhitelisted === true,
          isExpired
        }
      })
      const prevJSON = JSON.stringify(clientsRef.current.map(c => ({ id: c.accountId, plan: c.plan, active: c.subscriptionActive })))
      const newJSON = JSON.stringify(enriched.map(c => ({ id: c.accountId, plan: c.plan, active: c.subscriptionActive })))
      if (prevJSON !== newJSON) { clientsRef.current = enriched; setClients(enriched) }
    } catch (err) { console.error('Error fetching clients:', err) }
    finally { if (showLoading) setLoading(false) }
  }, [])

  // ── Computed ────────────────────────────────────────────

  const stats = useMemo(() => ({
    total: clients.length,
    active: clients.filter(c => c.hasActiveDevices).length,
    inactive: clients.filter(c => !c.hasActiveDevices).length,
    totalDevices: clients.reduce((sum, c) => sum + (c.deviceCount || 0), 0),
    subActive: clients.filter(c => c.subscriptionActive && !c.isTrial && !c.isWhitelisted).length,
    expired: clients.filter(c => c.isExpired && !c.isTrial).length,
    subInactive: clients.filter(c => !c.subscriptionActive && !c.isExpired).length,
    trial: clients.filter(c => c.isTrial).length,
    whitelisted: clients.filter(c => c.isWhitelisted).length,
    byPlan: {
      free: clients.filter(c => c.plan === 'free').length,
      basico: clients.filter(c => c.plan === 'basico').length,
      pro: clients.filter(c => c.plan === 'pro').length,
      comercial: clients.filter(c => c.plan === 'comercial').length,
    }
  }), [clients])

  const filteredClients = useMemo(() => {
    let filtered = [...clients]

    if (tab === 'overview') {
      if (filter === 'active') filtered = filtered.filter(c => c.hasActiveDevices)
      else if (filter === 'inactive') filtered = filtered.filter(c => !c.hasActiveDevices)
    } else {
      if (filter === 'active') filtered = filtered.filter(c => c.subscriptionActive && !c.isTrial && !c.isWhitelisted)
      else if (filter === 'expired') filtered = filtered.filter(c => c.isExpired && !c.isTrial)
      else if (filter === 'inactive') filtered = filtered.filter(c => !c.subscriptionActive && !c.isExpired)
      else if (filter === 'trial') filtered = filtered.filter(c => c.isTrial)
      else if (filter === 'whitelisted') filtered = filtered.filter(c => c.isWhitelisted)
    }

    if (searchTerm) {
      const s = searchTerm.toLowerCase()
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(s) ||
        c.email?.toLowerCase().includes(s) ||
        c.accountId?.toLowerCase().includes(s)
      )
    }
    return filtered
  }, [clients, filter, searchTerm, tab])

  const totalPages = Math.ceil(filteredClients.length / ITEMS_PER_PAGE)
  const paginatedClients = filteredClients.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  // ── Per-Client Billing ───────────────────────────────────
  const getClientBilling = useCallback((client) => {
    const devRevenue = (client.devices || []).reduce((sum, d) => {
      const econ = deviceEconomics[d.model]
      return sum + (econ ? econ.price : 0)
    }, 0)
    const devCost = (client.devices || []).reduce((sum, d) => {
      const econ = deviceEconomics[d.model]
      return sum + (econ ? econ.cost : 0)
    }, 0)
    const isPaying = client.subscriptionActive && !client.isTrial && !client.isWhitelisted
    const isAnnual = client.billingCycle === 'annual'
    const monthlyPrice = planPrices[client.plan] || 0
    const planPrice = isAnnual ? (ANNUAL_PLAN_PRICES[client.plan] || monthlyPrice * 12) : monthlyPrice
    // Estimate subscription periods: from registration to now
    let subPeriods = 0
    if (isPaying && client.registrationDate) {
      const regDate = new Date(client.registrationDate)
      const now = new Date()
      const totalMonths = Math.max(1, (now.getFullYear() - regDate.getFullYear()) * 12 + now.getMonth() - regDate.getMonth())
      subPeriods = isAnnual ? Math.max(1, Math.ceil(totalMonths / 12)) : totalMonths
    }
    const subRevenue = isPaying ? subPeriods * planPrice : 0
    return { devRevenue, devCost, subRevenue, total: devRevenue + subRevenue }
  }, [planPrices, deviceEconomics])
  // ── Warranty helpers ────────────────────────────────────

  // Merge manual warranties + auto-generated from device registrations (1 year)
  const allWarranties = useMemo(() => {
    const manual = [...warranties]
    const coveredMacs = new Set(manual.map(w => w.deviceMac))
    // For every device not covered by a manual warranty, auto-generate one
    const auto = []
    for (const c of clients) {
      for (const d of (c.devices || [])) {
        if (coveredMacs.has(d.mac)) continue
        const purchaseDate = d.createdAt ? new Date(d.createdAt).toISOString().split('T')[0] : null
        if (!purchaseDate) continue
        const expiresAt = new Date(d.createdAt)
        expiresAt.setFullYear(expiresAt.getFullYear() + 1)
        auto.push({
          $id: `auto-${d.mac}`,
          clientId: c.accountId,
          clientName: c.name || c.email || '',
          deviceMac: d.mac,
          deviceModel: d.model || 'Sin modelo',
          purchaseDate,
          warrantyMonths: 12,
          expiresAt: expiresAt.toISOString().split('T')[0],
          status: 'active',
          rmaHistory: '[]',
          notes: '',
          _auto: true
        })
      }
    }
    return [...manual, ...auto]
  }, [warranties, clients])

  const warrantyStats = useMemo(() => {
    const now = new Date()
    const active = allWarranties.filter(w => w.status === 'active' && new Date(w.expiresAt) > now)
    const expired = allWarranties.filter(w => w.status === 'active' && new Date(w.expiresAt) <= now)
    const rmaCount = allWarranties.reduce((s, w) => {
      try { return s + JSON.parse(w.rmaHistory || '[]').length } catch { return s }
    }, 0)
    const expiringSoon = allWarranties.filter(w => {
      if (w.status !== 'active') return false
      const exp = new Date(w.expiresAt)
      const diff = (exp - now) / (1000 * 60 * 60 * 24)
      return diff > 0 && diff <= 30
    })
    return { active: active.length, expired: expired.length, rmaCount, expiringSoon, total: allWarranties.length }
  }, [allWarranties])

  // ── Subscription Actions ────────────────────────────────

  const handleChangePlan = (client, newPlan) => {
    setPlanDropdown(null)
    if (newPlan === client.plan) return
    setConfirmModal({ show: true, client, action: 'changePlan', newPlan })
  }

  const handleToggleSubscription = (client) => {
    setConfirmModal({ show: true, client, action: client.subscriptionActive ? 'deactivate' : 'activate', newPlan: null })
  }

  const handleToggleTrial = (client) => {
    setConfirmModal({ show: true, client, action: client.isTrial ? 'removeTrial' : 'startTrial', newPlan: null, whitelistPlan: null })
  }

  const handleToggleWhitelist = (client) => {
    setConfirmModal({ show: true, client, action: client.isWhitelisted ? 'removeWhitelist' : 'addWhitelist', newPlan: null, whitelistPlan: client.plan || 'pro' })
  }

  const handleManualPayment = (client) => {
    setManualPayModal({ show: true, client, plan: client.plan !== 'free' ? client.plan : 'pro', billingCycle: 'monthly' })
  }

  const confirmManualPayment = async () => {
    if (!manualPayModal.client) return
    setProcessing(true)
    try {
      const { client, plan, billingCycle } = manualPayModal
      await SubscriptionService.registerManualPayment(client.accountId, plan, billingCycle)
      const expiresAt = new Date()
      if (billingCycle === 'monthly') expiresAt.setMonth(expiresAt.getMonth() + 1)
      else expiresAt.setFullYear(expiresAt.getFullYear() + 1)
      setClients(prev => prev.map(c => c.accountId === client.accountId ? {
        ...c,
        subscriptionActive: true,
        plan,
        isTrial: false,
        isWhitelisted: false,
        isExpired: false,
        manualPayment: true,
        billingCycle,
        subscriptionExpiresAt: expiresAt.toISOString(),
      } : c))
      setManualPayModal({ show: false, client: null, plan: 'pro', billingCycle: 'monthly' })
    } catch (error) {
      console.error('Error registering manual payment:', error)
      alert('Error al registrar cuota manual: ' + error.message)
    } finally { setProcessing(false) }
  }

  const confirmAction = async () => {
    if (!confirmModal.client) return
    setProcessing(true)
    try {
      const { client, action, newPlan } = confirmModal
      if (action === 'changePlan') {
        await SubscriptionService.changePlan(client.accountId, newPlan)
        setClients(prev => prev.map(c => c.accountId === client.accountId ? { ...c, plan: newPlan, subscriptionActive: newPlan !== 'free' } : c))
      } else if (action === 'addWhitelist') {
        await SubscriptionService.toggleWhitelist(client.accountId, true, confirmModal.whitelistPlan)
        setClients(prev => prev.map(c => c.accountId === client.accountId ? { ...c, isWhitelisted: true, subscriptionActive: true, plan: confirmModal.whitelistPlan, isTrial: false } : c))
      } else if (action === 'removeWhitelist') {
        await SubscriptionService.toggleWhitelist(client.accountId, false)
        setClients(prev => prev.map(c => c.accountId === client.accountId ? { ...c, isWhitelisted: false } : c))
      } else if (action === 'startTrial') {
        await SubscriptionService.toggleTrial(client.accountId, true, 'pro')
        const trialEnds = new Date(); trialEnds.setDate(trialEnds.getDate() + 7)
        setClients(prev => prev.map(c => c.accountId === client.accountId ? { ...c, subscriptionActive: true, plan: 'pro', isTrial: true, trialExpired: false, trialDaysRemaining: 7, subscriptionExpiresAt: trialEnds.toISOString() } : c))
      } else if (action === 'removeTrial') {
        await SubscriptionService.toggleTrial(client.accountId, false)
        setClients(prev => prev.map(c => c.accountId === client.accountId ? { ...c, subscriptionActive: false, plan: 'free', isTrial: false, trialExpired: false, trialDaysRemaining: null } : c))
      } else {
        const newStatus = action === 'activate'
        if (newStatus) {
          const result = await SubscriptionService.activateSubscription(client.accountId)
          // Backend syncs with MP on reactivation — use returned data
          setClients(prev => prev.map(c => c.accountId === client.accountId ? {
            ...c,
            subscriptionActive: true,
            isExpired: false,
            ...(result.plan ? { plan: result.plan } : {}),
            ...(result.subscriptionExpiresAt ? { subscriptionExpiresAt: result.subscriptionExpiresAt } : {}),
            ...(result.mercadopagoStatus ? { mercadopagoStatus: result.mercadopagoStatus } : {}),
          } : c))
        } else {
          await SubscriptionService.deactivateSubscription(client.accountId)
          setClients(prev => prev.map(c => c.accountId === client.accountId ? { ...c, subscriptionActive: false } : c))
        }
      }
      setConfirmModal({ show: false, client: null, action: null, newPlan: null, whitelistPlan: null })
    } catch (error) {
      console.error('Error updating subscription:', error)
      alert('Error al actualizar suscripción: ' + error.message)
    } finally { setProcessing(false) }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })
  }


  // ── Warranty CRUD ──
  const handleSaveWarranty = async () => {
    if (!warrantyForm) return
    setProcessing(true)
    try {
      if (warrantyForm.$id) {
        const { $id, ...data } = warrantyForm
        await ClientService.updateWarranty($id, data)
      } else {
        await ClientService.createWarranty(warrantyForm)
      }
      const res = await ClientService.listWarranties()
      setWarranties(res.warranties || [])
      setWarrantyForm(null)
    } catch (e) { console.error(e); alert('Error: ' + e.message) }
    finally { setProcessing(false) }
  }

  const handleDeleteWarranty = async (id) => {
    if (!confirm('¿Eliminar garantía?')) return
    try {
      await ClientService.deleteWarranty(id)
      setWarranties(prev => prev.filter(w => w.$id !== id))
    } catch (e) { alert('Error: ' + e.message) }
  }

  const handleAddRMA = async (warranty) => {
    const reason = prompt('Motivo del RMA:')
    if (!reason) return
    try {
      const history = JSON.parse(warranty.rmaHistory || '[]')
      history.push({ date: new Date().toISOString().split('T')[0], reason, status: 'pending' })
      await ClientService.updateWarranty(warranty.$id, { rmaHistory: JSON.stringify(history) })
      const res = await ClientService.listWarranties()
      setWarranties(res.warranties || [])
    } catch (e) { alert('Error: ' + e.message) }
  }

  // ── Note CRUD ──
  const handleSaveNote = async () => {
    if (!noteForm?.content) return
    setProcessing(true)
    try {
      await ClientService.createClientNote(noteForm)
      const res = await ClientService.listClientNotes()
      setClientNotes(res.notes || [])
      setNoteForm(null)
    } catch (e) { console.error(e); alert('Error: ' + e.message) }
    finally { setProcessing(false) }
  }

  const handleDeleteNote = async (id) => {
    if (!confirm('¿Eliminar nota?')) return
    try {
      await ClientService.deleteClientNote(id)
      setClientNotes(prev => prev.filter(n => n.$id !== id))
    } catch (e) { alert('Error: ' + e.message) }
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Cargando clientes...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="ch-page">
      {/* Header */}
      <div className="ch-header">
        <div>
          <h1 className="ch-title">Clientes</h1>
          <p className="ch-subtitle">Gestión de cuentas, dispositivos y suscripciones</p>
        </div>
        <div className="ch-header-actions">
          <button className="ch-export-btn" onClick={() => {
            const dateStr = new Date().toISOString().split('T')[0]
            if (tab === 'garantias') {
              exportToCSV(warranties, [
                { key: 'clientName', label: 'Cliente' },
                { key: 'deviceMac', label: 'MAC' },
                { key: 'deviceModel', label: 'Modelo' },
                { key: 'purchaseDate', label: 'Compra' },
                { key: 'expiresAt', label: 'Vence' },
                { key: 'status', label: 'Estado' },
              ], `garantias_${dateStr}`)
            } else {
              exportToCSV(filteredClients, [
                { key: 'name', label: 'Nombre' },
                { key: 'email', label: 'Email' },
                { key: 'accountId', label: 'Account ID' },
                { key: 'plan', label: 'Plan' },
                { key: 'subscriptionActive', label: 'Sub Activa', format: (v) => v ? 'Sí' : 'No' },
                { key: 'isTrial', label: 'Trial', format: (v) => v ? 'Sí' : 'No' },
                { key: 'isWhitelisted', label: 'Whitelist', format: (v) => v ? 'Sí' : 'No' },
                { key: 'deviceCount', label: 'Dispositivos' },
                { key: 'activeDeviceCount', label: 'Online' },
                { key: 'registrationDate', label: 'Registro', format: (v) => v ? new Date(v).toLocaleDateString('es-AR') : '' },
              ], `clientes_${dateStr}`)
            }
          }}>
            <Download size={16} /> Exportar CSV
          </button>
        </div>
      </div>

      {/* Tab Selector */}
      <div className="ch-tabs">
        <button className={`ch-tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => { setTab('overview'); setFilter('all') }}>
          <Users size={16} /> Cuentas
        </button>
        <button className={`ch-tab ${tab === 'subscriptions' ? 'active' : ''}`} onClick={() => { setTab('subscriptions'); setFilter('all') }}>
          <CreditCard size={16} /> Suscripciones
        </button>

        <button className={`ch-tab ${tab === 'garantias' ? 'active' : ''}`} onClick={() => { setTab('garantias'); setFilter('all') }}>
          <Shield size={16} /> Garantías
        </button>
        <button className={`ch-tab ${tab === 'crm' ? 'active' : ''}`} onClick={() => { setTab('crm'); setFilter('all') }}>
          <MessageSquare size={16} /> CRM
        </button>

      </div>

      {/* Stats Row */}
      {(tab === 'overview' || tab === 'subscriptions') && <div className="ch-stats">
        {tab === 'overview' ? (
          <>
            <div className="ch-stat" style={{ '--stat-accent': '#64748b' }}><span className="ch-stat-value">{stats.total}</span><span className="ch-stat-label">Total</span></div>
            <div className="ch-stat" style={{ '--stat-accent': '#10b981' }}><span className="ch-stat-value green">{stats.active}</span><span className="ch-stat-label">Con dispositivos</span></div>
            <div className="ch-stat" style={{ '--stat-accent': '#64748b' }}><span className="ch-stat-value">{stats.inactive}</span><span className="ch-stat-label">Sin actividad</span></div>
            <div className="ch-stat" style={{ '--stat-accent': '#a855f7' }}><span className="ch-stat-value purple">{stats.totalDevices}</span><span className="ch-stat-label">Dispositivos</span></div>
          </>
        ) : (
          <>
            <div className="ch-stat" style={{ '--stat-accent': '#10b981' }}><span className="ch-stat-value green">{stats.subActive}</span><span className="ch-stat-label">Activas</span></div>
            <div className="ch-stat" style={{ '--stat-accent': '#a855f7' }}><span className="ch-stat-value purple">{stats.trial}</span><span className="ch-stat-label">En prueba</span></div>
            <div className="ch-stat" style={{ '--stat-accent': '#14b8a6' }}><span className="ch-stat-value" style={{ color: '#14b8a6' }}>{stats.whitelisted}</span><span className="ch-stat-label">🛡️ Whitelist</span></div>
            <div className="ch-stat" style={{ '--stat-accent': '#f59e0b' }}><span className="ch-stat-value" style={{ color: '#f59e0b' }}>{stats.expired}</span><span className="ch-stat-label">Vencidas</span></div>
            <div className="ch-stat" style={{ '--stat-accent': '#ef4444' }}><span className="ch-stat-value red">{stats.subInactive}</span><span className="ch-stat-label">Bloqueadas</span></div>
            {Object.entries(stats.byPlan).filter(([, v]) => v > 0).map(([plan, count]) => (
              <div className="ch-stat" key={plan} style={{ '--stat-accent': PLANS[plan].color }}>
                <span className="ch-stat-value" style={{ color: PLANS[plan].color }}>{count}</span>
                <span className="ch-stat-label">{PLANS[plan].icon} {PLANS[plan].name}</span>
              </div>
            ))}
          </>
        )}
      </div>}

      {/* Filters */}
      {(tab === 'overview' || tab === 'subscriptions') && <div className="ch-filters">
        <div className="ch-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar por nombre, email o ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="ch-filter-btns">
          <button className={`ch-filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>Todos</button>
          <button className={`ch-filter-btn ${filter === 'active' ? 'active' : ''}`} onClick={() => setFilter('active')}>
            {tab === 'overview' ? 'Activos' : 'Activas'}
          </button>
          <button className={`ch-filter-btn ${filter === 'inactive' ? 'active' : ''}`} onClick={() => setFilter('inactive')}>
            {tab === 'overview' ? 'Inactivos' : 'Bloqueadas'}
          </button>
          {tab === 'subscriptions' && (
            <>
              <button className={`ch-filter-btn ${filter === 'trial' ? 'active' : ''}`} onClick={() => setFilter('trial')}>Trial</button>
              <button className={`ch-filter-btn ${filter === 'expired' ? 'active' : ''}`} onClick={() => setFilter('expired')}>Vencidas</button>
              <button className={`ch-filter-btn ${filter === 'whitelisted' ? 'active' : ''}`} onClick={() => setFilter('whitelisted')}>🛡️ Whitelist</button>
            </>
          )}
        </div>
        <span className="ch-filter-count">
          {filteredClients.length} de {clients.length}
        </span>
      </div>}

      {/* ── Overview Tab: Client Cards ── */}
      {tab === 'overview' && (
        <div className="ch-cards-grid">
          {paginatedClients.map(client => {
            const billing = getClientBilling(client)
            const devModels = (client.devices || []).reduce((acc, d) => {
              const m = d.model || 'Sin modelo'
              acc[m] = (acc[m] || 0) + 1
              return acc
            }, {})
            return (
              <div key={client.accountId} className="ch-client-card" onClick={() => setDetailClient(client)} style={{ cursor: 'pointer' }}>
                <div className="ch-card-header">
                  <div className="ch-card-name">{client.name || 'Sin nombre'}</div>
                  <span className={`ch-status ${client.hasActiveDevices ? 'online' : 'offline'}`}>
                    {client.hasActiveDevices ? '● Activo' : 'Inactivo'}
                  </span>
                </div>
                <div className="ch-card-email"><Mail size={13} /> {client.email || 'Sin email'}</div>
                <div className="ch-card-details">
                  <div className="ch-detail">
                    <Package size={14} />
                    <span>{client.deviceCount || 0} dispositivos</span>
                    {client.hasActiveDevices && <span className="green">({client.activeDeviceCount || 0} online)</span>}
                  </div>
                  {Object.keys(devModels).length > 0 && (
                    <div className="ch-detail ch-device-models">
                      {Object.entries(devModels).map(([model, count]) => (
                        <span key={model} className="ch-model-tag">{count}× {model}</span>
                      ))}
                    </div>
                  )}
                  <div className="ch-detail">
                    <CreditCard size={14} />
                    <span style={{ color: PLANS[client.plan]?.color }}>
                      {PLANS[client.plan]?.icon} {PLANS[client.plan]?.name || 'Sin plan'}
                    </span>
                    {client.isTrial && (
                      <span className="ch-trial-badge">
                        <Gift size={10} />
                        {client.trialExpired ? 'Expirado' : `${client.trialDaysRemaining ?? '?'}d`}
                      </span>
                    )}
                    {client.isWhitelisted && (
                      <span className="ch-whitelist-badge">
                        <Shield size={10} /> WL
                      </span>
                    )}
                    {client.subscriptionExpiresAt && !client.isWhitelisted && (
                      <span className="ch-expires-badge">
                        {new Date(client.subscriptionExpiresAt).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                  </div>
                  {billing.total > 0 && (
                    <div className="ch-billing">
                      <DollarSign size={14} />
                      <span className="ch-billing-total">${billing.total.toLocaleString('es-AR')}</span>
                      <span className="ch-billing-sep">—</span>
                      <span className="ch-billing-detail">
                        {billing.devRevenue > 0 && `Equipos $${billing.devRevenue.toLocaleString('es-AR')}`}
                        {billing.devRevenue > 0 && billing.subRevenue > 0 && ' · '}
                        {billing.subRevenue > 0 && `Sub $${billing.subRevenue.toLocaleString('es-AR')}`}
                      </span>
                    </div>
                  )}
                  {client.growRooms?.length > 0 && (
                    <div className="ch-rooms">
                      {client.growRooms.map((room, i) => (
                        <span key={i} className="ch-room-tag">🌱 {room.name}</span>
                      ))}
                    </div>
                  )}
                </div>
                {client.registrationDate && (
                  <div className="ch-card-footer">
                    <Calendar size={12} /> {formatDate(client.registrationDate)}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ── Subscriptions Tab: Table ── */}
      {tab === 'subscriptions' && (
        <div className="ch-table-wrap">
          <table className="ch-table">
            <thead>
              <tr>
                <th className="ch-col-user">Usuario</th>
                <th className="ch-col-email">Email</th>
                <th className="ch-col-devices">Dispositivos</th>
                <th className="ch-col-plan">Plan</th>
                <th className="ch-col-status">Estado</th>
                <th className="ch-col-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginatedClients.map(client => {
                const currentPlan = PLANS[client.plan] || PLANS.free
                return (
                  <tr key={client.accountId} onClick={() => { if (!planDropdown && !confirmModal.show) setDetailClient(client) }} style={{ cursor: 'pointer' }}>
                    <td data-label="Usuario">
                      <div className="ch-user-cell">
                        <span className="ch-user-name">{client.name || 'Sin nombre'}</span>
                        <span className="ch-user-id">{client.accountId?.substring(0, 12)}...</span>
                      </div>
                    </td>
                    <td data-label="Email" className="ch-dimmed">{client.email || '-'}</td>
                    <td data-label="Dispositivos">
                      <span className="ch-device-count">
                        {client.deviceCount || 0}
                        {client.hasActiveDevices && <span className="green"> ({client.activeDeviceCount || 0})</span>}
                      </span>
                    </td>
                    <td data-label="Plan" style={{ position: 'relative', overflow: 'visible' }}>
                      <button
                        className="ch-plan-btn"
                        style={{ border: `1px solid ${currentPlan.color}40`, background: `${currentPlan.color}15`, color: currentPlan.color }}
                        onClick={(e) => { e.stopPropagation(); setPlanDropdown(planDropdown === client.accountId ? null : client.accountId) }}
                      >
                        {currentPlan.icon} {currentPlan.name} <ChevronDown size={13} />
                      </button>
                      {planDropdown === client.accountId && (
                        <div className="ch-plan-dropdown">
                          {Object.entries(PLANS).map(([key, plan]) => (
                            <button key={key} className="ch-plan-option"
                              style={{ fontWeight: client.plan === key ? 700 : 400, color: client.plan === key ? plan.color : '#d1d5db', background: client.plan === key ? `${plan.color}20` : 'transparent' }}
                              onClick={(e) => { e.stopPropagation(); handleChangePlan(client, key) }}
                            >
                              {plan.icon} {plan.name} <span className="ch-plan-limit">{plan.devices}D/{plan.rooms}S</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td data-label="Estado">
                      {client.isWhitelisted ? (
                        <span className="ch-sub-badge whitelisted"><Shield size={13} /> Whitelist</span>
                      ) : client.isTrial ? (
                        <span className={`ch-sub-badge trial${client.trialExpired ? ' expired' : ''}`}>
                          <Gift size={13} /> {client.trialExpired ? 'Expirado' : `Trial ${client.trialDaysRemaining ?? '?'}d`}
                        </span>
                      ) : client.subscriptionActive ? (
                        <span className="ch-sub-badge active"><CheckCircle size={13} /> Activa</span>
                      ) : client.isExpired ? (
                        <span className="ch-sub-badge plan-expired"><Clock size={13} /> Vencida</span>
                      ) : (
                        <span className="ch-sub-badge inactive"><XCircle size={13} /> Bloqueada</span>
                      )}
                      {client.manualPayment && (
                        <span className="ch-sub-badge manual">💳 Manual</span>
                      )}
                      {client.subscriptionExpiresAt && !client.isWhitelisted && (
                        <span className="ch-sub-expires">
                          Vence: {new Date(client.subscriptionExpiresAt).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                    </td>
                    <td data-label="">
                      <div className="ch-actions" onClick={e => e.stopPropagation()}>
                        <button className={`ch-action-btn ${client.subscriptionActive ? 'danger' : 'success'}`}
                          onClick={() => handleToggleSubscription(client)}>
                          <Power size={12} /> {client.subscriptionActive ? 'Desactivar' : 'Activar'}
                        </button>
                        <button className={`ch-action-btn ${client.isTrial ? 'muted' : 'trial'}`}
                          onClick={() => handleToggleTrial(client)}>
                          <Gift size={12} /> {client.isTrial ? 'Quitar trial' : 'Dar trial'}
                        </button>
                        <button className={`ch-action-btn ${client.isWhitelisted ? 'whitelisted' : 'whitelist'}`}
                          onClick={() => handleToggleWhitelist(client)}>
                          <Shield size={12} /> {client.isWhitelisted ? '✕ Whitelist' : 'Whitelist'}
                        </button>
                        <button className="ch-action-btn manual-pay"
                          onClick={() => handleManualPayment(client)}>
                          <Wallet size={12} /> Cuota manual
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}



      {/* ── Garantías Tab ── */}
      {tab === 'garantias' && (
        <div className="biz-page">
          {/* KPIs */}
          <div className="biz-kpis">
            <div className="biz-kpi">
              <span className="biz-kpi-icon" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}><Shield size={18} /></span>
              <div>
                <div className="biz-kpi-value">{warrantyStats.active}</div>
                <div className="biz-kpi-label">Garantías activas</div>
              </div>
            </div>
            <div className="biz-kpi">
              <span className="biz-kpi-icon" style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}><XCircle size={18} /></span>
              <div>
                <div className="biz-kpi-value">{warrantyStats.expired}</div>
                <div className="biz-kpi-label">Vencidas</div>
              </div>
            </div>
            <div className="biz-kpi">
              <span className="biz-kpi-icon" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}><AlertTriangle size={18} /></span>
              <div>
                <div className="biz-kpi-value">{warrantyStats.expiringSoon.length}</div>
                <div className="biz-kpi-label">Vencen en 30 días</div>
              </div>
            </div>
            <div className="biz-kpi">
              <span className="biz-kpi-icon" style={{ background: 'rgba(139,92,246,0.12)', color: '#8b5cf6' }}><Package size={18} /></span>
              <div>
                <div className="biz-kpi-value">{warrantyStats.rmaCount}</div>
                <div className="biz-kpi-label">Total RMAs</div>
              </div>
            </div>
          </div>

          {/* New warranty button + form */}
          <div className="biz-card">
            <div className="gar-toolbar">
              <h3 className="biz-card-title"><Shield size={15} /> Garantías de Dispositivos</h3>
              <button className="ch-export-btn" onClick={() => setWarrantyForm({
                clientId: '', clientName: '', deviceMac: '', deviceModel: 'GP-BOX',
                purchaseDate: new Date().toISOString().split('T')[0], warrantyMonths: 12, notes: ''
              })}>
                <Plus size={16} /> Nueva Garantía
              </button>
            </div>

            {warrantyForm && (
              <div className="gar-form">
                <div className="gar-form-grid">
                  <div className="gar-field">
                    <label>Cliente</label>
                    <select value={warrantyForm.clientId} onChange={e => {
                      const c = clients.find(cl => cl.accountId === e.target.value)
                      setWarrantyForm(f => ({ ...f, clientId: e.target.value, clientName: c?.name || '' }))
                    }}>
                      <option value="">Seleccionar...</option>
                      {clients.map(c => <option key={c.accountId} value={c.accountId}>{c.name || c.email}</option>)}
                    </select>
                  </div>
                  <div className="gar-field">
                    <label>Dispositivo MAC</label>
                    <select value={warrantyForm.deviceMac} onChange={e => setWarrantyForm(f => ({ ...f, deviceMac: e.target.value }))}>
                      <option value="">Seleccionar...</option>
                      {warrantyForm.clientId && (clients.find(c => c.accountId === warrantyForm.clientId)?.devices || []).map(d => (
                        <option key={d.mac} value={d.mac}>{d.mac} ({d.model || 'Sin modelo'})</option>
                      ))}
                    </select>
                  </div>
                  <div className="gar-field">
                    <label>Modelo</label>
                    <select value={warrantyForm.deviceModel} onChange={e => setWarrantyForm(f => ({ ...f, deviceModel: e.target.value }))}>
                      <option value="GP-BOX">GP-BOX</option>
                      <option value="GP-MINI">GP-MINI</option>
                      <option value="GP-CAM">GP-CAM</option>
                      <option value="GP-HIDRO">GP-HIDRO</option>
                      <option value="GP-RELAY">GP-RELAY</option>
                      <option value="GP-DIM">GP-DIM</option>
                    </select>
                  </div>
                  <div className="gar-field">
                    <label>Fecha de compra</label>
                    <input type="date" value={warrantyForm.purchaseDate || ''} onChange={e => setWarrantyForm(f => ({ ...f, purchaseDate: e.target.value }))} />
                  </div>
                  <div className="gar-field">
                    <label>Meses de garantía</label>
                    <input type="number" min="1" max="60" value={warrantyForm.warrantyMonths || 12} onChange={e => setWarrantyForm(f => ({ ...f, warrantyMonths: Number(e.target.value) }))} />
                  </div>
                  <div className="gar-field">
                    <label>Notas</label>
                    <input type="text" placeholder="Notas..." value={warrantyForm.notes || ''} onChange={e => setWarrantyForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                </div>
                <div className="gar-form-actions">
                  <button className="ch-modal-cancel" onClick={() => setWarrantyForm(null)}>Cancelar</button>
                  <button className="ch-modal-confirm" onClick={handleSaveWarranty} disabled={processing || !warrantyForm.clientId || !warrantyForm.deviceMac}>
                    {processing ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </div>
            )}

            {/* Warranties table */}
            <div className="biz-sales-table-wrap">
              <table className="biz-sales-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Dispositivo</th>
                    <th>Modelo</th>
                    <th>Compra</th>
                    <th>Vence</th>
                    <th>Estado</th>
                    <th>RMAs</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {allWarranties.length === 0 ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>No hay garantías registradas</td></tr>
                  ) : (
                    allWarranties.map(w => {
                      const now = new Date()
                      const expires = new Date(w.expiresAt)
                      const daysLeft = Math.ceil((expires - now) / (1000 * 60 * 60 * 24))
                      const rmas = (() => { try { return JSON.parse(w.rmaHistory || '[]') } catch { return [] } })()
                      const isExpired = daysLeft <= 0
                      return (
                        <tr key={w.$id} className={isExpired ? 'gar-expired-row' : daysLeft <= 30 ? 'gar-warning-row' : ''}>
                          <td>{w.clientName || w.clientId} {w._auto && <span className="gar-auto-badge">Auto</span>}</td>
                          <td className="gar-mac">{w.deviceMac}</td>
                          <td>{w.deviceModel}</td>
                          <td>{formatDate(w.purchaseDate)}</td>
                          <td>{formatDate(w.expiresAt)}</td>
                          <td>
                            <span className={`an-health-badge ${isExpired ? 'critical' : daysLeft <= 30 ? 'risk' : 'thriving'}`}>
                              {isExpired ? 'Vencida' : daysLeft <= 30 ? `${daysLeft}d restantes` : 'Activa'}
                            </span>
                          </td>
                          <td>
                            {rmas.length > 0 ? (
                              <span className="gar-rma-count" title={rmas.map(r => `${r.date}: ${r.reason}`).join('\n')}>{rmas.length} RMA{rmas.length > 1 ? 's' : ''}</span>
                            ) : '—'}
                          </td>
                          <td className="gar-actions">
                            {!w._auto && (
                              <>
                                <button className="inv-icon-btn" title="Registrar RMA" onClick={() => handleAddRMA(w)}>
                                  <Package size={14} />
                                </button>
                                <button className="inv-icon-btn danger" title="Eliminar" onClick={() => handleDeleteWarranty(w.$id)}>
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                            {w._auto && (
                              <button className="inv-icon-btn" title="Convertir a garantía manual (para editar/RMA)" onClick={() => {
                                const { _auto, $id, ...data } = w
                                setWarrantyForm(data)
                              }}>
                                <Edit3 size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Expiring soon list */}
          {warrantyStats.expiringSoon.length > 0 && (
            <div className="biz-card">
              <h3 className="biz-card-title"><Clock size={15} /> Garantías por vencer (30 días)</h3>
              <div className="biz-list">
                {warrantyStats.expiringSoon.map(w => {
                  const daysLeft = Math.ceil((new Date(w.expiresAt) - new Date()) / (1000 * 60 * 60 * 24))
                  return (
                    <div key={w.$id} className="biz-list-row">
                      <div>
                        <div className="biz-list-name">{w.clientName || w.clientId} — {w.deviceMac}</div>
                        <div className="biz-list-sub">{w.deviceModel} · Comprado {formatDate(w.purchaseDate)}</div>
                      </div>
                      <span className={`biz-expiry-badge ${daysLeft <= 7 ? 'urgent' : 'soon'}`}>{daysLeft}d</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── CRM Tab ── */}
      {tab === 'crm' && (
        <div className="biz-page">
          {/* Client selector + timeline */}
          <div className="biz-grid">
            <div className="biz-card">
              <h3 className="biz-card-title"><Users size={15} /> Seleccionar Cliente</h3>
              <div className="crm-search">
                <Search size={16} />
                <input type="text" placeholder="Buscar cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              </div>
              <div className="crm-client-list">
                {clients
                  .filter(c => !searchTerm || (c.name || c.email || '').toLowerCase().includes(searchTerm.toLowerCase()))
                  .slice(0, 20)
                  .map(c => {
                    const noteCount = clientNotes.filter(n => n.clientId === c.accountId).length
                    const warCount = warranties.filter(w => w.clientId === c.accountId).length
                    const ticketCount = supportTickets.filter(t => t.clientId === c.accountId || (t.clientEmail && t.clientEmail === c.email)).length
                    const openTickets = supportTickets.filter(t => (t.clientId === c.accountId || (t.clientEmail && t.clientEmail === c.email)) && (t.status === 'open' || t.status === 'in_progress')).length
                    return (
                      <div key={c.accountId} className={`crm-client-item ${selectedClient?.accountId === c.accountId ? 'active' : ''}`} onClick={() => setSelectedClient(c)}>
                        <div>
                          <div className="crm-client-name">{c.name || c.email || 'Sin nombre'}</div>
                          <div className="crm-client-meta">{PLANS[c.plan]?.icon} {PLANS[c.plan]?.name} · {c.deviceCount || 0} device(s)</div>
                        </div>
                        <div className="crm-client-badges">
                          {noteCount > 0 && <span className="crm-badge notes">{noteCount}</span>}
                          {warCount > 0 && <span className="crm-badge warranty">{warCount}</span>}
                          {ticketCount > 0 && <span className={`crm-badge ticket ${openTickets > 0 ? 'open' : ''}`}>{ticketCount}</span>}
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>

            <div className="biz-card biz-card-flex">
              {!selectedClient ? (
                <div className="biz-empty" style={{ padding: '3rem' }}>
                  <Users size={32} style={{ opacity: 0.3 }} />
                  <span>Seleccioná un cliente para ver su timeline</span>
                </div>
              ) : (
                <>
                  {/* Client header */}
                  <div className="crm-header">
                    <div>
                      <h3 className="crm-client-title">{selectedClient.name || selectedClient.email}</h3>
                      <div className="crm-client-detail">
                        <span>{selectedClient.email}</span>
                        <span>{PLANS[selectedClient.plan]?.icon} {PLANS[selectedClient.plan]?.name}</span>
                        <span>{selectedClient.deviceCount || 0} dispositivo(s)</span>
                        <span>Registro: {formatDate(selectedClient.registrationDate)}</span>
                      </div>
                    </div>
                    <button className="ch-export-btn" onClick={() => setNoteForm({
                      clientId: selectedClient.accountId, clientName: selectedClient.name || selectedClient.email,
                      type: 'note', content: ''
                    })}>
                      <Plus size={16} /> Nueva Nota
                    </button>
                  </div>

                  {/* Note form */}
                  {noteForm && (
                    <div className="crm-note-form">
                      <div className="crm-note-form-row">
                        <select value={noteForm.type} onChange={e => setNoteForm(f => ({ ...f, type: e.target.value }))}>
                          <option value="note">📝 Nota</option>
                          <option value="call">📞 Llamada</option>
                          <option value="email">📧 Email</option>
                          <option value="meeting">🤝 Reunión</option>
                          <option value="issue">⚠️ Incidencia</option>
                        </select>
                        <input type="text" placeholder="Contenido de la nota..." value={noteForm.content} onChange={e => setNoteForm(f => ({ ...f, content: e.target.value }))} className="crm-note-input" />
                      </div>
                      <div className="crm-note-form-actions">
                        <button className="ch-modal-cancel" onClick={() => setNoteForm(null)}>Cancelar</button>
                        <button className="ch-modal-confirm" onClick={handleSaveNote} disabled={processing || !noteForm.content}>
                          {processing ? 'Guardando...' : 'Guardar'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Unified Timeline — sorted chronologically */}
                  <div className="crm-timeline">
                    {(() => {
                      // Build unified timeline events
                      const events = []
                      // Registration
                      if (selectedClient.registrationDate) {
                        events.push({ id: 'reg', type: 'auto', date: selectedClient.registrationDate, dot: '#3b82f6', icon: '🎉', label: 'Registro', deletable: false })
                      }
                      // Devices
                      ; (selectedClient.devices || []).forEach((d, i) => {
                        events.push({ id: `dev-${i}`, type: 'auto', date: d.createdAt, dot: '#10b981', icon: '📦', label: `Dispositivo registrado: ${d.model || 'Sin modelo'} (${d.mac})`, deletable: false })
                      })
                      // Warranties (manual + auto)
                      allWarranties.filter(w => w.clientId === selectedClient.accountId).forEach(w => {
                        events.push({ id: `war-${w.$id}`, type: 'auto', date: w.purchaseDate, dot: '#8b5cf6', icon: '🛡️', label: `Garantía: ${w.deviceModel} (${w.deviceMac}) — ${w.warrantyMonths} meses`, deletable: false })
                      })
                      // Support tickets
                      const statusLabels = { open: 'Abierto', in_progress: 'En Progreso', resolved: 'Resuelto', closed: 'Cerrado' }
                      const priorityIcons = { high: '🔴', medium: '🟡', low: '🟢' }
                      supportTickets
                        .filter(t => t.clientId === selectedClient.accountId || (t.clientEmail && t.clientEmail === selectedClient.email))
                        .forEach(t => {
                          events.push({
                            id: `tk-${t.$id}`, type: 'ticket', date: t.createdAt || t.$createdAt, dot: t.status === 'open' || t.status === 'in_progress' ? '#ef4444' : '#6b7280',
                            icon: '🎫', label: `${priorityIcons[t.priority] || '🟡'} ${t.ticketNumber}: ${t.subject}`,
                            sublabel: `${statusLabels[t.status] || t.status} · ${t.category || 'general'}`,
                            ticketStatus: t.status, deletable: false
                          })
                        })
                      // Manual notes
                      const noteIcons = { note: '📝', call: '📞', email: '📧', meeting: '🤝', issue: '⚠️' }
                      clientNotes.filter(n => n.clientId === selectedClient.accountId).forEach(n => {
                        events.push({ id: n.$id, type: 'manual', date: n.createdAt, dot: '#f59e0b', icon: noteIcons[n.type] || '📝', label: n.content, deletable: true })
                      })
                      // Sort by date descending (newest first)
                      events.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))

                      if (events.length === 0) {
                        return <div className="biz-empty"><span>Sin actividad registrada</span></div>
                      }

                      return events.map(ev => (
                        <div key={ev.id} className={`crm-event ${ev.type}`}>
                          <div className="crm-event-dot" style={{ background: ev.dot }} />
                          <div className="crm-event-content">
                            <div className="crm-event-type">{ev.icon} {ev.label}</div>
                            {ev.sublabel && <div className="crm-event-sublabel">{ev.sublabel}</div>}
                            <div className="crm-event-date">{formatDate(ev.date)}</div>
                          </div>
                          {ev.deletable && (
                            <button className="crm-event-delete" onClick={() => handleDeleteNote(ev.id)} title="Eliminar">
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      ))
                    })()}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {(tab === 'overview' || tab === 'subscriptions') && filteredClients.length === 0 && (
        <div className="ch-empty">
          <Users size={40} style={{ opacity: 0.3 }} />
          <p>No se encontraron clientes con los filtros aplicados</p>
        </div>
      )}

      {/* Pagination */}
      {(tab === 'overview' || tab === 'subscriptions') && filteredClients.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredClients.length}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="ch-modal-overlay">
          <div className="ch-modal">
            <div className="ch-modal-header">
              <AlertTriangle size={24} style={{ color: '#f59e0b' }} />
              <h3>Confirmar Acción</h3>
            </div>
            <div className="ch-modal-body">
              {confirmModal.action === 'changePlan' && (
                <>Cambiar plan de <strong>{confirmModal.client?.name || confirmModal.client?.email}</strong> a{' '}
                  <span style={{ color: PLANS[confirmModal.newPlan]?.color, fontWeight: 700 }}>
                    {PLANS[confirmModal.newPlan]?.icon} {PLANS[confirmModal.newPlan]?.name}
                  </span>?</>
              )}
              {confirmModal.action === 'startTrial' && <>Activar <strong>7 días de prueba Pro</strong> para <strong>{confirmModal.client?.name}</strong>?</>}
              {confirmModal.action === 'removeTrial' && <>Quitar trial de <strong>{confirmModal.client?.name}</strong>? La cuenta será desactivada.</>}
              {confirmModal.action === 'deactivate' && <>Desactivar suscripción de <strong>{confirmModal.client?.name}</strong>? Se bloquearán {confirmModal.client?.deviceCount || 0} dispositivo(s).</>}
              {confirmModal.action === 'activate' && <>Activar suscripción de <strong>{confirmModal.client?.name}</strong>?</>}
              {confirmModal.action === 'addWhitelist' && <>Agregar a <strong>{confirmModal.client?.name}</strong> a la <strong>whitelist</strong>? Tendrá plan <strong style={{ color: '#14b8a6' }}>{PLANS[confirmModal.whitelistPlan]?.name || 'Pro'}</strong> sin pagar, sin expiración.</>}
              {confirmModal.action === 'removeWhitelist' && <>Quitar whitelist de <strong>{confirmModal.client?.name}</strong>? La suscripción seguirá activa pero ya no estará protegida.</>}
            </div>
            <div className="ch-modal-actions">
              <button className="ch-modal-cancel" onClick={() => setConfirmModal({ show: false, client: null, action: null, newPlan: null, whitelistPlan: null })} disabled={processing}>Cancelar</button>
              <button className="ch-modal-confirm" onClick={confirmAction} disabled={processing}
                style={{ background: ['deactivate', 'removeTrial', 'removeWhitelist'].includes(confirmModal.action) ? '#ef4444' : '#10b981' }}>
                {processing ? 'Procesando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Payment Modal */}
      {manualPayModal.show && (
        <div className="ch-modal-overlay">
          <div className="ch-modal ch-modal-manual-pay">
            <div className="ch-modal-header">
              <CreditCard size={24} style={{ color: '#3b82f6' }} />
              <h3>Registrar Cuota Manual</h3>
            </div>
            <div className="ch-modal-body">
              <p>Registrar pago por transferencia para <strong>{manualPayModal.client?.name || manualPayModal.client?.email}</strong></p>
              <div className="ch-manual-pay-form">
                <label className="ch-manual-pay-label">Plan</label>
                <div className="ch-manual-pay-options">
                  {['basico', 'pro', 'comercial'].map(p => (
                    <button key={p} className={`ch-manual-pay-plan ${manualPayModal.plan === p ? 'selected' : ''}`}
                      style={{ borderColor: manualPayModal.plan === p ? PLANS[p].color : '#374151', color: manualPayModal.plan === p ? PLANS[p].color : '#9ca3af' }}
                      onClick={() => setManualPayModal(prev => ({ ...prev, plan: p }))}>
                      {PLANS[p].icon} {PLANS[p].name}
                    </button>
                  ))}
                </div>
                <label className="ch-manual-pay-label">Período</label>
                <div className="ch-manual-pay-options">
                  <button className={`ch-manual-pay-cycle ${manualPayModal.billingCycle === 'monthly' ? 'selected' : ''}`}
                    onClick={() => setManualPayModal(prev => ({ ...prev, billingCycle: 'monthly' }))}>
                    📅 1 Mes
                  </button>
                  <button className={`ch-manual-pay-cycle ${manualPayModal.billingCycle === 'annual' ? 'selected' : ''}`}
                    onClick={() => setManualPayModal(prev => ({ ...prev, billingCycle: 'annual' }))}>
                    📆 1 Año
                  </button>
                </div>
                <div className="ch-manual-pay-summary">
                  <span>Vencimiento:</span>
                  <strong>{(() => {
                    const d = new Date()
                    if (manualPayModal.billingCycle === 'monthly') d.setMonth(d.getMonth() + 1)
                    else d.setFullYear(d.getFullYear() + 1)
                    return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })
                  })()}</strong>
                </div>
              </div>
            </div>
            <div className="ch-modal-actions">
              <button className="ch-modal-cancel" onClick={() => setManualPayModal({ show: false, client: null, plan: 'pro', billingCycle: 'monthly' })} disabled={processing}>Cancelar</button>
              <button className="ch-modal-confirm" onClick={confirmManualPayment} disabled={processing} style={{ background: '#3b82f6' }}>
                {processing ? 'Procesando...' : 'Registrar Cuota'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client Detail Panel */}
      {detailClient && (
        <ClientDetail
          client={detailClient}
          onClose={() => setDetailClient(null)}
          warranties={allWarranties}
          notes={clientNotes}
          tickets={supportTickets}
          billing={getClientBilling(detailClient)}
          onToggleSubscription={(c) => { setDetailClient(null); handleToggleSubscription(c) }}
          onToggleTrial={(c) => { setDetailClient(null); handleToggleTrial(c) }}
          onToggleWhitelist={(c) => { setDetailClient(null); handleToggleWhitelist(c) }}
          onManualPayment={(c) => { setDetailClient(null); handleManualPayment(c) }}
          onChangePlan={(c, plan) => { setDetailClient(null); handleChangePlan(c, plan) }}
          onAddNote={(c) => {
            setDetailClient(null)
            setTab('crm')
            setSelectedClient(c)
            setNoteForm({ clientId: c.accountId, clientName: c.name || c.email, type: 'note', content: '' })
          }}
          onDeleteNote={handleDeleteNote}
          formatDate={formatDate}
        />
      )}
    </div>
  )
}
